import React, { useState } from 'react';
import { Search, Plus, ChevronDown, User, List, MessageSquare, Settings, Menu, X, BarChart2, Globe } from 'lucide-react';

const BRAND_LOGO_URL = 'https://i.postimg.cc/44mFB7xb/b82d446d_ea30_4c43_a9b9_caf02c25e896_removalai_preview.png';

const Navbar = ({ user, onSignIn, onRegister, onSignOut, onProfileClick, onListingsClick, onChatsClick, onSettingsClick, onAnalyticsClick, onSearch, onListProperty, searching }) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showBrandMenu, setShowBrandMenu] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const handleSearch = (e) => {
    e.preventDefault();
    if (onSearch) {
      onSearch(searchQuery);
    }
  };

  const handleListProperty = () => {
    if (onListProperty) {
      onListProperty();
    }
  };

  return (
    <div className="sticky top-3 sm:top-4 z-50 px-3 sm:px-6 mb-2 sm:mb-1">
      <nav className="bg-white/80 backdrop-blur-md shadow-lg rounded-full max-w-7xl mx-auto">
        <div className="relative px-3 sm:px-6 py-3 sm:py-0">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0 sm:h-16">
            <div className="flex items-center gap-2 sm:justify-start sm:flex-none">
              <div className="flex-shrink-0 relative">
                <button
                  type="button"
                  onClick={() => setShowBrandMenu((prev) => !prev)}
                  className="flex items-center cursor-pointer outline-none"
                  aria-label="GhanaStay menu"
                >
                  <img
                    src={BRAND_LOGO_URL}
                    alt="GhanaStay"
                    className="h-9 w-16 sm:h-12 sm:w-24 object-contain object-left"
                  />
                  <ChevronDown className="w-4 h-4 text-gray-500" />
                </button>
                {showBrandMenu && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setShowBrandMenu(false)} />
                    <div className="absolute left-0 top-full mt-2 w-52 bg-white rounded-xl shadow-lg border border-gray-100 py-2 z-50">
                      <button
                        onClick={() => {
                          window.open('https://booking.ghanastay.app/en', '_blank', 'noopener');
                          setShowBrandMenu(false);
                        }}
                        className="w-full flex items-center gap-2.5 px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 transition-colors"
                      >
                        <Globe className="w-4 h-4 text-blue-600" />
                        <span>GhanaStay Booking</span>
                      </button>
                    </div>
                  </>
                )}
              </div>
              <div className="flex-1 min-w-0 sm:hidden">
                <form onSubmit={handleSearch} className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Search className="h-4 w-4 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search..."
                    className="w-full pl-9 pr-16 py-2 text-sm rounded-full border border-gray-200 focus:border-blue-500 focus:outline-none transition-colors duration-200 text-gray-700 placeholder-gray-400 bg-white/50"
                  />
                  <button
                    type="submit"
                    disabled={searching}
                    className="absolute right-1 top-1/2 -translate-y-1/2 px-2.5 py-1.5 bg-gradient-to-r from-blue-600 to-blue-500 text-white text-[10px] font-semibold rounded-full hover:from-blue-700 hover:to-blue-600 transition-all disabled:opacity-70 disabled:cursor-not-allowed"
                  >
                    Go
                  </button>
                </form>
              </div>
              <button
                onClick={() => setShowMobileMenu((prev) => !prev)}
                className="sm:hidden flex items-center justify-center w-10 h-10 rounded-full border border-blue-100 text-blue-600 hover:bg-blue-50 transition-colors duration-200 flex-shrink-0"
              >
                {showMobileMenu ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </button>
            </div>

            <div className="hidden sm:block w-full sm:flex-1 sm:max-w-xl sm:mx-6 order-3 sm:order-none">
              <form onSubmit={handleSearch} className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-4 w-4 text-gray-400" />
                </div>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search properties, locations..."
                  className="w-full pl-10 pr-20 sm:pr-24 py-2 text-sm rounded-full border border-gray-200 focus:border-blue-500 focus:outline-none transition-colors duration-200 text-gray-700 placeholder-gray-400 bg-white/50"
                />
                <button
                  type="submit"
                  disabled={searching}
                  className="absolute right-1 top-1/2 -translate-y-1/2 px-3 sm:px-4 py-1.5 bg-gradient-to-r from-blue-600 to-blue-500 text-white text-[11px] sm:text-xs font-semibold rounded-full hover:from-blue-700 hover:to-blue-600 transition-all disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  {searching ? 'Searching...' : 'Search'}
                </button>
              </form>
            </div>

            <div className="hidden sm:flex items-center justify-end sm:justify-start space-x-2 sm:space-x-3 order-2 sm:order-none">
              <button 
                onClick={handleListProperty}
                className="hidden sm:flex items-center space-x-2 px-4 py-2 text-sm rounded-full text-blue-600 font-semibold hover:bg-blue-50 transition-colors duration-200"
              >
                <Plus className="h-4 w-4" />
                <span>List a property</span>
              </button>

              {user ? (
                <div className="relative">
                  <button
                    onClick={() => setShowDropdown(!showDropdown)}
                    className="flex items-center space-x-2 px-2 sm:px-3 py-2 rounded-full hover:bg-blue-50 transition-colors duration-200"
                  >
                    <img
                      src={user.photo || 'https://via.placeholder.com/32'}
                      alt={user.name}
                      className="w-8 h-8 rounded-full border-2 border-blue-400"
                    />
                    <span className="hidden sm:inline text-sm font-semibold text-gray-700">{user.firstName}</span>
                    <ChevronDown className="h-4 w-4 text-gray-500" />
                  </button>

                  {showDropdown && (
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-lg border border-gray-100 py-2 z-50">
                      <button
                        onClick={() => {
                          onProfileClick();
                          setShowDropdown(false);
                        }}
                        className="w-full flex items-center space-x-3 px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 transition-colors"
                      >
                        <User className="h-4 w-4 text-blue-600" />
                        <span>Profile</span>
                      </button>
                      <button
                        onClick={() => {
                          onListingsClick();
                          setShowDropdown(false);
                        }}
                        className="w-full flex items-center space-x-3 px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 transition-colors"
                      >
                        <List className="h-4 w-4 text-blue-600" />
                        <span>Listings</span>
                      </button>
                      <button
                        onClick={() => {
                          onChatsClick();
                          setShowDropdown(false);
                        }}
                        className="w-full flex items-center space-x-3 px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 transition-colors"
                      >
                        <MessageSquare className="h-4 w-4 text-blue-600" />
                        <span>Chats</span>
                      </button>
                      <button
                        onClick={() => {
                          onAnalyticsClick();
                          setShowDropdown(false);
                        }}
                        className="w-full flex items-center space-x-3 px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 transition-colors"
                      >
                        <BarChart2 className="h-4 w-4 text-blue-600" />
                        <span>Analytics</span>
                      </button>
                      <button
                        onClick={() => {
                          onSettingsClick();
                          setShowDropdown(false);
                        }}
                        className="w-full flex items-center space-x-3 px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 transition-colors"
                      >
                        <Settings className="h-4 w-4 text-blue-600" />
                        <span>Settings</span>
                      </button>
                      <hr className="my-2 border-gray-100" />
                      <button
                        onClick={onSignOut}
                        className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                      >
                        Sign Out
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <button
                    onClick={onSignIn}
                    className="px-3 sm:px-5 py-2 text-xs sm:text-sm rounded-full border-2 border-blue-600 text-blue-600 font-semibold hover:bg-blue-50 transition-colors duration-200"
                  >
                    Sign In
                  </button>
                  <button
                    onClick={onRegister}
                    className="px-3 sm:px-5 py-2 text-xs sm:text-sm rounded-full bg-gradient-to-r from-blue-600 to-blue-400 text-white font-semibold hover:from-blue-700 hover:to-blue-500 transition-all duration-200 shadow-md hover:shadow-lg"
                  >
                    Register
                  </button>
                </>
              )}
            </div>
          </div>

          {showMobileMenu && (
            <div className="sm:hidden absolute left-3 right-3 top-full mt-3 z-50">
              <div className="rounded-3xl border border-blue-100 bg-white/95 backdrop-blur-md shadow-2xl p-3 flex flex-col gap-2">
                <button
                  onClick={() => {
                    handleListProperty();
                    setShowMobileMenu(false);
                  }}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-2xl bg-blue-50 text-blue-600 font-semibold"
                >
                  <Plus className="h-4 w-4" />
                  <span>List a property</span>
                </button>

                {user ? (
                  <>
                    <button
                      onClick={() => {
                        onProfileClick();
                        setShowMobileMenu(false);
                      }}
                      className="w-full flex items-center gap-3 px-4 py-3 rounded-2xl text-sm text-gray-700 hover:bg-blue-50 transition-colors"
                    >
                      <User className="h-4 w-4 text-blue-600" />
                      <span>Profile</span>
                    </button>
                    <button
                      onClick={() => {
                        onListingsClick();
                        setShowMobileMenu(false);
                      }}
                      className="w-full flex items-center gap-3 px-4 py-3 rounded-2xl text-sm text-gray-700 hover:bg-blue-50 transition-colors"
                    >
                      <List className="h-4 w-4 text-blue-600" />
                      <span>Listings</span>
                    </button>
                    <button
                      onClick={() => {
                        onChatsClick();
                        setShowMobileMenu(false);
                      }}
                      className="w-full flex items-center gap-3 px-4 py-3 rounded-2xl text-sm text-gray-700 hover:bg-blue-50 transition-colors"
                    >
                      <MessageSquare className="h-4 w-4 text-blue-600" />
                      <span>Chats</span>
                    </button>
                    <button
                      onClick={() => {
                        onAnalyticsClick();
                        setShowMobileMenu(false);
                      }}
                      className="w-full flex items-center gap-3 px-4 py-3 rounded-2xl text-sm text-gray-700 hover:bg-blue-50 transition-colors"
                    >
                      <BarChart2 className="h-4 w-4 text-blue-600" />
                      <span>Analytics</span>
                    </button>
                    <button
                      onClick={() => {
                        onSettingsClick();
                        setShowMobileMenu(false);
                      }}
                      className="w-full flex items-center gap-3 px-4 py-3 rounded-2xl text-sm text-gray-700 hover:bg-blue-50 transition-colors"
                    >
                      <Settings className="h-4 w-4 text-blue-600" />
                      <span>Settings</span>
                    </button>
                    <button
                      onClick={() => {
                        onSignOut();
                        setShowMobileMenu(false);
                      }}
                      className="w-full flex items-center gap-3 px-4 py-3 rounded-2xl text-sm text-red-600 hover:bg-red-50 transition-colors"
                    >
                      <span>Sign Out</span>
                    </button>
                  </>
                ) : (
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={() => {
                        onSignIn();
                        setShowMobileMenu(false);
                      }}
                      className="px-4 py-3 text-sm rounded-2xl border-2 border-blue-600 text-blue-600 font-semibold hover:bg-blue-50 transition-colors duration-200"
                    >
                      Sign In
                    </button>
                    <button
                      onClick={() => {
                        onRegister();
                        setShowMobileMenu(false);
                      }}
                      className="px-4 py-3 text-sm rounded-2xl bg-gradient-to-r from-blue-600 to-blue-400 text-white font-semibold hover:from-blue-700 hover:to-blue-500 transition-all duration-200 shadow-md"
                    >
                      Register
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </nav>
      <div className="h-0.5 bg-gradient-to-r from-transparent via-blue-500 to-transparent max-w-7xl mx-auto mt-2"></div>
    </div>
  );
};

export default Navbar;
